import { apiClient } from './client';
import type { Order, OrderFilters, OrderDetail } from '@/types/models';
import type { OrderStatus } from '@/types/enums';
import type { BackendPaginatedResponse } from '@/types/api';

export const ordersApi = {
  getList: async (filters?: OrderFilters): Promise<BackendPaginatedResponse<Order>> => {
    const params: Record<string, unknown> = {};

    if (filters?.search?.trim()) params.search = filters.search.trim();
    if (filters?.status) params.status = filters.status;
    if (filters?.page !== undefined) params.page = filters.page;
    if (filters?.limit !== undefined) params.limit = filters.limit;

    const response = await apiClient.get<BackendPaginatedResponse<Order>>('/v1/orders', { params });
    return response.data;
  },

  getById: async (id: number): Promise<OrderDetail> => {
    const response = await apiClient.get<OrderDetail>(`/v1/orders/${id}`);
    return response.data;
  },

  changeStatus: async (id: number, status: OrderStatus): Promise<OrderDetail> => {
    const response = await apiClient.patch<OrderDetail>(`/v1/orders/${id}/status`, { status });
    return response.data;
  },

  updateOrder: async (id: number, data: { shelf_location?: string | null; notes?: string | null }): Promise<OrderDetail> => {
    const response = await apiClient.patch<OrderDetail>(`/v1/orders/${id}`, data);
    return response.data;
  },

  assignShelf: async (id: number, shelf_location: string): Promise<{ order_id: number; shelf_location: string; order_number: string }> => {
    const response = await apiClient.patch<{ order_id: number; shelf_location: string; order_number: string }>(`/v1/orders/${id}/shelf`, { shelf_location });
    return response.data;
  },

  deleteOrder: async (id: number): Promise<void> => {
    await apiClient.delete(`/v1/orders/${id}`);
  },

  getMyOrders: async (filters?: OrderFilters): Promise<BackendPaginatedResponse<Order>> => {
    const params: Record<string, unknown> = {};

    if (filters?.search?.trim()) params.search = filters.search.trim();
    if (filters?.status) params.status = filters.status;
    if (filters?.page !== undefined) params.page = filters.page;
    if (filters?.limit !== undefined) params.limit = filters.limit;

    const response = await apiClient.get<BackendPaginatedResponse<Order>>('/v1/my/orders', { params });
    return response.data;
  },

  getMyOrderById: async (id: number): Promise<OrderDetail> => {
    const response = await apiClient.get<OrderDetail>(`/v1/my/orders/${id}`);
    return response.data;
  },

  getMyOrderQrBlob: async (id: number): Promise<Blob> => {
    const response = await apiClient.get(`/v1/my/orders/${id}/qr`, { responseType: 'blob' });
    return response.data as Blob;
  },

  downloadInvoicePdf: async (orderId: number): Promise<Blob> => {
    const response = await apiClient.get(`/v1/orders/${orderId}/invoice-pdf`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },
};
